import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../shared/auth/AuthContext';
import { uuidv4 } from '../../shared/utils/uuid';
import { apiClient, ApiError } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Input,
  Modal,
  Pagination,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';

/* ── Types ── */

type TerminalStatus = 'ACTIVE' | 'INACTIVE' | 'BLOCKED' | 'REVOKED';

interface Terminal {
  id: string;
  code: string;
  name: string;
  status: TerminalStatus;
  lastActivityAt: string | null;
  createdAt: string;
}

interface TerminalPage {
  items: Terminal[];
  total: number;
  offset: number;
  limit: number;
}

interface CreateTerminalPayload {
  code: string;
  name: string;
  serialNumber: string;
  publicKey: string;
  branchId: string;
}

/* ── Status helpers ── */

const statusConfig: Record<TerminalStatus, { label: string; variant: 'success' | 'error' | 'warning' | 'neutral' }> = {
  ACTIVE: { label: 'Ativo', variant: 'success' },
  INACTIVE: { label: 'Inativo', variant: 'neutral' },
  BLOCKED: { label: 'Bloqueado', variant: 'warning' },
  REVOKED: { label: 'Revogado', variant: 'error' },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '---';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ── Component ── */

export function TerminalsPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const pageSize = 10;

  /* ── Create modal state ── */
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [formErrors, setFormErrors] = useState<{ code?: string; name?: string }>({});

  /* ── Revoke confirm state ── */
  const [revokeTarget, setRevokeTarget] = useState<Terminal | null>(null);

  /* ── Delete confirm state ── */
  const [deleteTarget, setDeleteTarget] = useState<Terminal | null>(null);

  /* ── Fetch terminals ── */
  const offset = (page - 1) * pageSize;

  const {
    data,
    isLoading,
    error,
  } = useQuery<TerminalPage, ApiError>({
    queryKey: [...queryKeys.pos.terminals(), offset, pageSize],
    queryFn: () =>
      apiClient.get<TerminalPage>('/pos/terminals', {
        params: { offset: String(offset), limit: String(pageSize) },
      }),
    enabled: !!tenantId,
  });

  const totalPages = data ? Math.ceil(data.total / pageSize) : 1;

  /* ── Create terminal ── */
  const createMutation = useMutation<Terminal, ApiError, CreateTerminalPayload>({
    mutationFn: (payload) => apiClient.post<Terminal>('/pos/terminals', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pos.terminals() });
      addToast('Terminal cadastrado com sucesso', 'success');
      setShowCreateModal(false);
      resetForm();
    },
    onError: (err) => {
      addToast(err.message || 'Erro ao cadastrar terminal', 'error');
    },
  });

  /* ── Revoke terminal ── */
  const revokeMutation = useMutation<void, ApiError, string>({
    mutationFn: (id) => apiClient.post<void>(`/pos/terminals/${id}/revoke`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pos.terminals() });
      addToast('Terminal revogado com sucesso', 'success');
      setRevokeTarget(null);
    },
    onError: (err) => {
      addToast(err.message || 'Erro ao revogar terminal', 'error');
    },
  });

  /* ── Delete terminal ── */
  const deleteMutation = useMutation<void, ApiError, string>({
    mutationFn: (id) => apiClient.delete<void>(`/pos/terminals/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pos.terminals() });
      addToast('Terminal excluído com sucesso', 'success');
      setDeleteTarget(null);
    },
    onError: (err) => {
      addToast(err.message || 'Erro ao excluir terminal', 'error');
    },
  });

  /* ── Helpers ── */
  function resetForm(): void {
    setNewCode('');
    setNewName('');
    setFormErrors({});
  }

  function validateForm(): boolean {
    const errors: { code?: string; name?: string } = {};
    if (!newCode.trim()) errors.code = 'O código é obrigatório';
    if (!newName.trim()) errors.name = 'O nome é obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleCreate(): void {
    if (!validateForm()) return;
    if (!tenantId) return;

    createMutation.mutate({
      code: newCode.trim(),
      name: newName.trim(),
      serialNumber: uuidv4(),
      publicKey: uuidv4(),
      branchId: tenantId,
    });
  }

  /* ── Loading state ── */
  if (isLoading) {
    return (
      <div className="terminals-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Terminais PDV</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Terminais de PDV e seu status.</p>
          </div>
        </div>
        <Card variant="elevated" padding="1.5rem">
          <CardBody>
            <LoadingSkeleton height="40px" count={5} />
          </CardBody>
        </Card>
      </div>
    );
  }

  /* ── Error state ── */
  if (error) {
    return (
      <div className="terminals-page">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">Terminais PDV</h1>
            <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Terminais de PDV e seu status.</p>
          </div>
        </div>
        <Card variant="elevated" padding="1.5rem">
          <CardBody>
            <div className="state-error" role="alert">
              <p className="error-title">Erro ao carregar terminais</p>
              <p className="error-message">{error.message}</p>
              <Button
                variant="outline"
                onClick={() => queryClient.invalidateQueries({ queryKey: queryKeys.pos.terminals() })}
              >
                Tentar novamente
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  const terminals = data?.items ?? [];

  return (
    <div className="terminals-page">
      {/* Page header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Terminais PDV</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Gerencie os terminais de ponto de venda do sistema.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            + Novo Terminal
          </Button>
        </div>
      </div>

      {/* Main card */}
      <Card variant="elevated" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {terminals.length} {terminals.length === 1 ? 'terminal' : 'terminais'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Terminais cadastrados</h3>
        </CardHeader>

        {/* Empty state */}
        {terminals.length === 0 && (
          <CardBody>
            <div className="state-empty">
              <p>Nenhum terminal cadastrado ainda.</p>
              <p className="empty-hint">Clique em &quot;+ Novo Terminal&quot; para cadastrar o primeiro.</p>
            </div>
          </CardBody>
        )}

        {/* Data table */}
        {terminals.length > 0 && (
          <>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Último acesso</th>
                    <th style={{ width: '200px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {terminals.map((terminal) => {
                    const status = statusConfig[terminal.status];
                    return (
                      <tr key={terminal.id}>
                        <td><code className="terminal-code">{terminal.code}</code></td>
                        <td>{terminal.name}</td>
                        <td>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </td>
                        <td className="date-cell">{formatDate(terminal.lastActivityAt)}</td>
                        <td>
                          <div className="actions-cell">
                            {terminal.status === 'ACTIVE' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setRevokeTarget(terminal)}
                                loading={revokeMutation.isPending && revokeTarget?.id === terminal.id}
                              >
                                Revogar
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteTarget(terminal)}
                              loading={deleteMutation.isPending && deleteTarget?.id === terminal.id}
                            >
                              Excluir
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-wrapper">
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </div>
            )}
          </>
        )}
      </Card>

      {/* ── Create Modal ── */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); resetForm(); }}
        title="Novo Terminal"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => { setShowCreateModal(false); resetForm(); }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={handleCreate}
              loading={createMutation.isPending}
            >
              Cadastrar
            </Button>
          </div>
        }
      >
        <div className="create-form">
          <Input
            label="Código"
            placeholder="Ex: PDV-001"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            error={formErrors.code}
            disabled={createMutation.isPending}
          />
          <Input
            label="Nome"
            placeholder="Ex: Caixa 1"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            error={formErrors.name}
            disabled={createMutation.isPending}
          />
        </div>
      </Modal>

      {/* ── Revoke Confirmation Modal ── */}
      <Modal
        isOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        title="Revogar Terminal"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setRevokeTarget(null)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
              loading={revokeMutation.isPending}
            >
              Confirmar Revogação
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, color: '#191c1e', fontSize: '0.875rem', lineHeight: 1.6 }}>
          Tem certeza que deseja revogar o terminal{' '}
          <strong>{revokeTarget?.code} - {revokeTarget?.name}</strong>?
          Após revogado, o terminal não poderá mais operar até ser reativado.
        </p>
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <Modal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Excluir Terminal"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setDeleteTarget(null)}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              fullWidth
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              loading={deleteMutation.isPending}
            >
              Confirmar Exclusão
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, color: '#191c1e', fontSize: '0.875rem', lineHeight: 1.6 }}>
          Tem certeza que deseja excluir permanentemente o terminal{' '}
          <strong>{deleteTarget?.code} - {deleteTarget?.name}</strong>?
          Esta ação não pode ser desfeita.
        </p>
      </Modal>

      {/* ── Styles ── */}
      <style>{`
        .terminals-page {
          padding: 1.5rem;
        }

        .subtitle {
          color: #43474e;
          font-size: 0.875rem;
          margin: 0;
        }

        .table-scroll {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .data-table th {
          text-align: left;
          padding: 0.75rem 1rem;
          background: #f2f4f6;
          color: #43474e;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5;
          white-space: nowrap; position: sticky; top: 0; z-index: 1;}

        .data-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #eceef0;
          color: #191c1e;
          vertical-align: middle;
        }

        .data-table tbody tr:hover td {
          background: #eceef0;
        }

        .terminal-code {
          font-size: 0.8125rem;
          background: #f2f4f6;
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
          color: #191c1e;
        }

        .date-cell {
          color: #43474e;
          font-size: 0.8125rem;
        }

        .actions-cell {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .pagination-wrapper {
          display: flex;
          justify-content: center;
          padding: 1rem;
          border-top: 1px solid #eceef0;
        }

        .create-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .state-error {
          text-align: center;
          padding: 2rem 1rem;
        }

        .error-title {
          color: #ba1a1a;
          font-weight: 600;
          margin: 0 0 0.5rem;
        }

        .error-message {
          color: #74777f;
          font-size: 0.875rem;
          margin: 0 0 1.25rem;
        }

        .state-empty {
          text-align: center;
          padding: 3rem 1rem;
          color: #74777f;
        }

        .state-empty p {
          margin: 0;
          font-size: 0.875rem;
        }

        .empty-hint {
          margin-top: 0.5rem !important;
          font-size: 0.8125rem !important;
        }
      `}</style>
    </div>
  );
}
